/**
 * ConversationRegistry — connection-scoped, status-aware registry of live
 * conversation Chat instances. Replaces ChatInstanceRegistry.
 *
 * Differences from the ref-counted ChatInstanceRegistry it supersedes:
 *   - Each entry carries a `status` ('idle' | 'running' | 'done' | 'needs-approval')
 *     and `lastActivityAt` (for LRU + grace eviction).
 *   - A `running` or `needs-approval` entry's Chat is NEVER disposed, even at
 *     refCount 0 — a background run keeps draining the firehose into its kept-alive
 *     Chat after its tab unmounts, and a paused tool-approval is not lost.
 *   - A `done` entry's Chat is RETAINED as "warm" at refCount 0 (switch-back renders
 *     from it with no refetch) until evict() drops it (grace timer / LRU).
 *
 * CRITICAL (design §8.2): this registry holds the Chat — NOT a parallel chunk/message
 * buffer. Chat.messages is the single source of truth. Do not add a second message store.
 *
 * Generic over the held value (must expose dispose(): void) so it unit-tests
 * without React/Chat.
 */
export type ConversationStatus = 'idle' | 'running' | 'done' | 'needs-approval';

interface Entry<T> {
  chat: T | null; // null after eviction — a cold/lightweight liveness entry
  status: ConversationStatus;
  lastActivityAt: number;
  refCount: number;
}

export interface ConversationRegistryOptions {
  /** Max warm (non-running) Chats retained in memory. Default 8. */
  warmCap?: number;
}

export class ConversationRegistry<T extends { dispose: () => void }> {
  private readonly entries = new Map<string, Entry<T>>();
  private readonly warmCap: number;

  constructor(options: ConversationRegistryOptions = {}) {
    this.warmCap = options.warmCap ?? 8;
  }

  /** Acquire (creating via factory on first call) the Chat for `id`. Increments refCount. */
  acquire(id: string, factory: () => T): T {
    const existing = this.entries.get(id);
    if (existing) {
      existing.refCount += 1;
      if (existing.chat === null) existing.chat = factory(); // re-warm a cold entry
      existing.lastActivityAt = Date.now();
      return existing.chat;
    }
    const chat = factory();
    this.entries.set(id, { chat, status: 'idle', lastActivityAt: Date.now(), refCount: 1 });
    return chat;
  }

  /**
   * Drop one reference. Disposal policy:
   *   - running        → NEVER disposed (background run continues).
   *   - done/idle/etc. → RETAINED as warm at refCount 0 (switch-back uses it).
   * Actual disposal happens only via evict() (grace timer or LRU).
   */
  release(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (entry.refCount > 0) entry.refCount -= 1;
  }

  /** Set the liveness status; bumps lastActivityAt. */
  setStatus(id: string, status: ConversationStatus): void {
    const entry = this.entries.get(id);
    if (!entry) {
      this.entries.set(id, { chat: null, status, lastActivityAt: Date.now(), refCount: 0 });
      return;
    }
    entry.status = status;
    entry.lastActivityAt = Date.now();
  }

  getStatus(id: string): ConversationStatus | undefined {
    return this.entries.get(id)?.status;
  }

  getChat(id: string): T | null {
    return this.entries.get(id)?.chat ?? null;
  }

  getLastActivityAt(id: string): number {
    return this.entries.get(id)?.lastActivityAt ?? 0;
  }

  /**
   * Drop the in-memory Chat for `id` (becomes cold) but keep the lightweight
   * liveness/badge entry. NO-OP if the entry is running or already cold or has
   * live references. A switch-back to an evicted entry is a cold GET /messages.
   */
  evict(id: string): void {
    const entry = this.entries.get(id);
    if (!entry || entry.chat === null) return;
    // Never evict an active run: 'running' is mid-stream; 'needs-approval' is
    // paused on a tool-approval request whose pending decision would be lost
    // (design §8.3 / §8.4). Both survive their tab unmounting.
    if (entry.status === 'running' || entry.status === 'needs-approval' || entry.refCount > 0) {
      return;
    }
    entry.chat.dispose();
    entry.chat = null;
  }

  /**
   * Enforce the warm-Chat cap. Evicts least-recently-active warm chats
   * (non-running, refCount 0, chat present) until at-or-under the cap.
   */
  enforceLru(): void {
    // The cap bounds the TOTAL number of in-memory Chats (warmSize), but only
    // non-running, unreferenced chats are eligible for eviction. A running chat
    // counts against the cap yet is never dropped — so a workspace full of
    // background runs simply stops evicting rather than killing a live run.
    const liveCount = this.warmSize();
    const evictable = [...this.entries.entries()]
      .filter(
        ([, e]) =>
          e.chat !== null &&
          e.status !== 'running' &&
          e.status !== 'needs-approval' &&
          e.refCount === 0,
      )
      .sort((a, b) => a[1].lastActivityAt - b[1].lastActivityAt); // oldest first
    let over = liveCount - this.warmCap;
    for (const [id] of evictable) {
      if (over <= 0) break;
      this.evict(id);
      over -= 1;
    }
  }

  /** Test/diagnostic: number of entries holding a live Chat. */
  warmSize(): number {
    let n = 0;
    for (const e of this.entries.values()) if (e.chat !== null) n += 1;
    return n;
  }

  /**
   * @internal Test-only diagnostic: returns the own-keys of the Entry for `id`.
   * Used to assert no parallel message-buffer field was added to Entry (design
   * §8.2 — Chat.messages is the single source of truth, not a registry buffer).
   */
  getEntryKeysForTest(id: string): string[] {
    const entry = this.entries.get(id);
    if (!entry) return [];
    return Object.keys(entry);
  }
}
